import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  CustomCategoriesPageDocument,
  CustomCategoryDetailDocument,
  FinanceCategoryLedgerAccountInput,
  FinanceCustomCategoryItemFragment,
  FinanceCustomCategoryLegalEntityFragment,
  UpdateFinanceCustomCategoryDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useLedgerAccountAssignment } from 'modules/finance/hooks/accounting/useLedgerAccountAssignment'

import LedgerAccountAssigmentForm from '../LedgerAccountAssignmentForm'

interface EditCustomCategoryForm {
  label: string
  assignedLedgerAccounts: FinanceCategoryLedgerAccountInput[]
}

interface Props {
  category: FinanceCustomCategoryItemFragment
  legalEntities: FinanceCustomCategoryLegalEntityFragment[]
  canEditLedgerAccountAssignments: boolean
  onClose: () => void
}

const EditCustomCategoryModal = ({
  category,
  legalEntities,
  canEditLedgerAccountAssignments,
  onClose,
}: Props) => {
  const { data: assignedLedgerAccountsData } = useQuery(CustomCategoryDetailDocument, {
    variables: { resourceIds: [category.id] },
  })

  const assignedLedgerAccountResources =
    assignedLedgerAccountsData?.finance.ledgerAccountResourcesConnection?.nodes ?? []

  const [updateCategory] = useMutation(UpdateFinanceCustomCategoryDocument, {
    extractorFn: (data) => data.financeMutations.updateFinanceCustomCategory,
  })

  const currentResourceAssignments: FinanceCategoryLedgerAccountInput[] =
    assignedLedgerAccountResources.map((assignedResource) => ({
      ledgerAccountId: assignedResource.account.id,
      legalEntityId: Number(assignedResource.account.legalEntityId),
      ledgerAccountResourceId: assignedResource.id,
    }))

  const form = useForm<EditCustomCategoryForm>({
    defaultValues: {
      label: category.label,
      assignedLedgerAccounts: currentResourceAssignments,
    },
    validate: () => {
      const errors: FormErrors<EditCustomCategoryForm> = {}
      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await updateCategory({
        variables: {
          id: category.id,
          label: data.label,
          deleted: false,
          enabled: category.enabled,
          position: 0,
          visible: true,
          ledgerAccountAssignments: data.assignedLedgerAccounts,
        },
        refetchQueries: [CustomCategoriesPageDocument],
      }).then((res) => {
        track(trackingEvents.accounting.settings.categories.editCategory)
        return res
      })

      const handled = handleResult(result, (errors) => {
        setErrors(errors)
        app.setFlash({
          type: 'wrong',
          message: errors.messages || i18n.t('errors.generic_error'),
        })
      })
      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

        handleClose()
      }
      return handled
    },
  })

  const handleSelectLedgerAccount = useLedgerAccountAssignment(form.setValues)

  const handleSave = async () => {
    form.handleSubmit()
  }

  const handleClose = () => {
    onClose()
  }

  return (
    <Modal
      illustration="document-edit"
      title={i18n.t('finance.settings.accounting.custom_categories.modals.edit_category.title')}
      footer={
        <>
          <Button type="secondary" onClick={handleClose}>
            {i18n.t('finance.settings.accounting.custom_categories.modals.buttons.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={form.isSubmitting || !form.values.label}>
            {i18n.t('finance.settings.accounting.custom_categories.modals.buttons.update')}
          </Button>
        </>
      }
      onClose={onClose}
    >
      <Box gap="s16" minHeight="s200">
        <TextField
          label={i18n.t(
            'finance.settings.accounting.custom_categories.modals.edit_category.fields.name'
          )}
          value={category.defaultLabel}
          mandatory
          readonly
        />
        <TextField
          label={i18n.t(
            'finance.settings.accounting.custom_categories.modals.edit_category.fields.alias'
          )}
          onChange={(value) => form.setValues({ ...form.values, label: value })}
          value={form.values.label}
          error={form.errors.label}
        />
        {canEditLedgerAccountAssignments && (
          <LedgerAccountAssigmentForm
            legalEntities={legalEntities}
            currentAssignments={currentResourceAssignments.map((assignment) => ({
              ...assignment,
              operationType: undefined,
            }))}
            onChange={handleSelectLedgerAccount}
          />
        )}
      </Box>
    </Modal>
  )
}

export default EditCustomCategoryModal
